import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { botServerApi } from '../services/api';
import { authApi, isAuthenticated } from '../services/auth';

const REFRESH_INTERVAL_MS = 30000;

const normalizeServers = (data) => {
  const payload = data?.response ?? data?.data?.response ?? data?.data ?? data;
  if (Array.isArray(payload)) {
    return payload;
  }
  if (Array.isArray(payload?.data)) {
    return payload.data;
  }
  return [];
};

const formatRelativeTime = (value) => {
  if (!value) {
    return 'Never';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'Unknown';
  }

  const diffSeconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  if (diffSeconds < 10) {
    return 'Just now';
  }
  if (diffSeconds < 60) {
    return `${diffSeconds}s ago`;
  }

  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60) {
    return `${diffMinutes}m ago`;
  }

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) {
    return `${diffHours}h ago`;
  }

  return date.toLocaleString();
};

const getStatusClass = (status) => {
  switch (status) {
    case 'ONLINE':
      return 'bg-success-100 text-success-800';
    case 'DEGRADED':
      return 'bg-warning-100 text-warning-800';
    case 'OFFLINE':
      return 'bg-error-100 text-error-800';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

const getLoadClass = (ratio) => {
  if (ratio >= 0.9) {
    return 'bg-error-500';
  }
  if (ratio >= 0.7) {
    return 'bg-warning-500';
  }
  return 'bg-success-500';
};

const BotServers = () => {
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login');
    }
  }, [navigate]);

  const fetchServers = useCallback(async ({ silent = false } = {}) => {
    try {
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const response = await botServerApi.getServers();
      setServers(normalizeServers(response.data));
    } catch (err) {
      console.error('Error fetching bot servers:', err);
      setError('Failed to load bot servers. Please try again.');

      if (err.response?.status === 401) {
        authApi.logout();
        navigate('/login');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchServers();
  }, [fetchServers]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      fetchServers({ silent: true });
    }, REFRESH_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [fetchServers]);

  const summary = useMemo(() => {
    return servers.reduce(
      (acc, server) => {
        const status = server.status || 'UNKNOWN';
        acc.total += 1;
        acc.online += status === 'ONLINE' ? 1 : 0;
        acc.offline += status === 'OFFLINE' ? 1 : 0;
        acc.running += Number(server.currentRunning || 0);
        acc.capacity += Number(server.maxConcurrent || 0);
        return acc;
      },
      { total: 0, online: 0, offline: 0, running: 0, capacity: 0 },
    );
  }, [servers]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-title-md font-semibold text-gray-900 dark:text-white/90">
              Bot Servers
            </h1>
            <span className="inline-flex items-center rounded-full bg-brand-50 px-2.5 py-1 text-xs font-semibold text-brand-700">
              {summary.online}/{summary.total} online
            </span>
          </div>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            Monitor auto-tool workers, capacity, categories, and heartbeat freshness.
          </p>
        </div>

        <button
          onClick={() => fetchServers({ silent: true })}
          disabled={loading || refreshing}
          className="autobot-button-secondary"
        >
          <svg className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-theme-sm">
          <p className="text-sm font-semibold text-gray-500">Servers</p>
          <p className="mt-2 text-title-sm font-semibold text-gray-900">{summary.total}</p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-theme-sm">
          <p className="text-sm font-semibold text-gray-500">Online</p>
          <p className="mt-2 text-title-sm font-semibold text-success-600">{summary.online}</p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-theme-sm">
          <p className="text-sm font-semibold text-gray-500">Offline</p>
          <p className="mt-2 text-title-sm font-semibold text-error-600">{summary.offline}</p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-theme-sm">
          <p className="text-sm font-semibold text-gray-500">Load</p>
          <p className="mt-2 text-title-sm font-semibold text-gray-900">
            {summary.running}/{summary.capacity || 0}
          </p>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-error-200 bg-error-50 px-4 py-3 text-error-700">
          <div className="flex items-center justify-between gap-4">
            <span className="text-sm font-semibold">{error}</span>
            <button onClick={() => fetchServers()} className="text-sm font-semibold text-error-700 hover:text-error-900">
              Retry
            </button>
          </div>
        </div>
      )}

      <div className="rounded-lg border border-gray-200 bg-white shadow-theme-sm">
        <div className="border-b border-gray-200 px-6 py-4">
          <h3 className="text-lg font-semibold text-gray-900">Registered Servers</h3>
        </div>

        {loading ? (
          <div className="flex h-64 items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
          </div>
        ) : servers.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <h3 className="text-sm font-semibold text-gray-900">No bot servers found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Start an auto-tool worker with heartbeat enabled to register it here.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase text-gray-500">Server</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase text-gray-500">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase text-gray-500">Load</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase text-gray-500">Categories</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase text-gray-500">Last Seen</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase text-gray-500">Last Error</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {servers.map((server) => {
                  const current = Number(server.currentRunning || 0);
                  const max = Number(server.maxConcurrent || 0);
                  const ratio = max > 0 ? current / max : 0;
                  const categories = server.supportedCategories || server.supported_categories || [];

                  return (
                    <tr key={server.id || server.name} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="font-semibold text-gray-900">{server.name || 'Unnamed server'}</div>
                        <div className="max-w-xs truncate text-xs text-gray-500">{server.baseUrl || server.base_url || '-'}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${getStatusClass(server.status)}`}>
                          {server.status || 'UNKNOWN'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="min-w-[120px]">
                          <div className="mb-1 flex items-center justify-between text-xs font-semibold text-gray-600">
                            <span>{current}/{max}</span>
                            <span>{max > 0 ? `${Math.round(ratio * 100)}%` : '0%'}</span>
                          </div>
                          <div className="h-2 rounded-full bg-gray-100">
                            <div
                              className={`h-2 rounded-full ${getLoadClass(ratio)}`}
                              style={{ width: `${Math.min(100, Math.round(ratio * 100))}%` }}
                            />
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex max-w-md flex-wrap gap-1.5">
                          {categories.length ? categories.map((category) => (
                            <span key={category} className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-700">
                              {category}
                            </span>
                          )) : (
                            <span className="text-sm text-gray-500">-</span>
                          )}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600">
                        {formatRelativeTime(server.lastSeenAt || server.last_seen_at)}
                      </td>
                      <td className="px-6 py-4">
                        <span className="block max-w-xs truncate text-sm text-gray-500">
                          {server.lastError || server.last_error || '-'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default BotServers;
